import { fetchSubProductsOrNominals } from '@api/useQueryCalls';
import BPNoDataFound from '@atoms/BPNoDataFound';
import { Button, View } from '@components/primitives';
import useBPMutation from '@customHooks/useBPRequest';
import useFavourites from '@customHooks/useFavourites';
import { useToast } from '@hooks';
import RequestTransactionSheet from '@molecules/RequestTransactionSheet';
import { useNavigation, RouteProp } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import { showErrorOnAPI } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchAvailableProviderFromResponse,
  getDisabledCheckForTagihanPLN,
  refetchBPIfStale,
  requestBPForTagihanPLN,
  shouldShowAccountInput,
} from '../BPUtils';
import { logErrorOnAnalytics, logPageBack, logPageLaunch } from '../GGNominals/analyticsNominals';
import { scale } from '@utils/normalize';
// ---------- TYPES ----------
interface ProductConfig {
  productId: number;
  productName: string;
  noOfColumns?: number;
  hasAccountInput?: boolean;
}

interface ProductApiData {
  prefix?: string;
  [key: string]: any;
}

interface RouteParams {
  source: string;
  productConfig: ProductConfig;
  productApiData: ProductApiData;
}

type GGTagihanPLNRoute = RouteProp<{ params: RouteParams }, 'params'>;

interface GGTagihanPLNProps {
  route: GGTagihanPLNRoute;
}

// ---------- EMPTY VIEW HELPER ----------
export function shouldShowEmptyView(showEmptyView: boolean) {
  return showEmptyView ? <BPNoDataFound /> : null;
}

// ---------- COMPONENT ----------
const GGTagihanPLN: React.FC<GGTagihanPLNProps> = ({ route }) => {
  const { setLoading, setInitialData, selectedBPAccount, validatedReferralCode, meAmplitudeContext } = getStore();

  const { accountTypeId } = selectedBPAccount;
  const { productConfig, productApiData, source } = route.params;
  const { productId, productName } = productConfig;

  const [openRTS, setOpenRTS] = useState(false);
  const [showEmptyView, setShowEmptyView] = useState(false);
  const [sAccountInputError, setAccountInputError] = useState('');
  const [bpReqData, setBpReqData] = useState<Record<string, any>>({});
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(null);
  const [availablePostPaidProvider, setAvailablePostPaidProvider] = useState<Record<string, any>>({});

  const { t } = useTranslation('common');
  const toast = useToast();
  const navigation = useNavigation();

  const { requestBP, isBPReqLoading, isBPReqError, bpError, apiPayload } = useBPMutation({
    setOpenRTS,
    setBpReqData,
  });

  const { isLoading, isSuccess, data, isError, error, isStale, refetch } = fetchSubProductsOrNominals({
    productId,
    accountTypeId,
  });

  useEffect(() => {
    navigation.setOptions?.({ title: productName });
  }, [productName]);

  const isFavourite = useFavourites(
    navigation,
    productApiData?.id,
    productApiData?.isDefaultFavorites || productApiData?.isFavorites,
    source,
    productApiData?.prefix,
    productApiData?.canAddFavorites,
  );

  useEffect(() => {
    logPageLaunch(productConfig, source, validatedReferralCode, isFavourite);
    refetchBPIfStale(isStale, refetch);
    return () => logPageBack(productConfig, source, validatedReferralCode);
  }, []);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    showErrorOnAPI(isError, error, toast, t, navigation, setInitialData);
  }, [isError, error]);

  useEffect(() => {
    fetchAvailableProviderFromResponse(isSuccess, data, setAvailablePostPaidProvider, setShowEmptyView);
  }, [isSuccess, data]);

  useEffect(() => {
    logErrorOnAnalytics(
      isBPReqError,
      selectedAccountNumber,
      productConfig,
      apiPayload,
      t,
      bpError,
      source,
      validatedReferralCode,
      isFavourite,
    );
  }, [isBPReqError, bpError]);

  return (
    <View style={appStyles.safeAreaView}>
      {shouldShowAccountInput({
        productConfig,
        productApiData,
        selectedAccountNumber,
        setSelectedAccountNumber,
        sAccountInputError,
        setAccountInputError,
      })}
      <View style={appStyles.flex1} />
      {shouldShowEmptyView(showEmptyView)}
      <View
        style={[
          appStyles.borderWithShadow,
          appStyles.marginHorizontal(0),
          appStyles.marginTop(0),
          appStyles.paddingHorizontal(0),
          appStyles.paddingTop(0),
          appStyles.borderTopLeftRadius(0),
          appStyles.borderTopRightRadius(0),
          appStyles.borderBottomLeftRadius(0),
          appStyles.borderBottomRightRadius(0),
        ]}>
        <Button
          testID="btnConfirm"
          isDisabled={getDisabledCheckForTagihanPLN(selectedAccountNumber, showEmptyView)}
          isLoading={isBPReqLoading}
          onPress={requestBPForTagihanPLN({
            selectedAccountNumber,
            selectedBPAccount,
            productApiData,
            availablePostPaidProvider,
            validatedReferralCode,
            requestBP,
            setAccountInputError,
            t,
            productConfig,
            source,
            meAmplitudeContext,
            isFavourite,
          })}
          style={[appStyles.marginHorizontal(16), appStyles.marginVertical(16), { paddingVertical: scale(8) }]}>
          {t('common.confirmed')}
        </Button>
        <RequestTransactionSheet
          openRTS={openRTS}
          setOpenRTS={setOpenRTS}
          productConfig={productConfig}
          productApiData={productApiData}
          selectedAccountNumber={selectedAccountNumber}
          nominalSelected={availablePostPaidProvider}
          bpReqData={bpReqData}
        />
      </View>
    </View>
  );
};

export default GGTagihanPLN;
